import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
type PageView = 'home' | 'archive' | 'detail' | 'configurator' | 'warranty' | 'instructions' | 'contact' | 'media' | 'dealers';
interface MediaPageProps {
  onNavigate: (page: PageView, productId?: number) => void;
}
interface Article {
  id: number;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  image: string;
  category: string;
}
const articles: Article[] = [{
  id: 1,
  title: 'Installing Your First Lift Kit: A Complete Guide',
  excerpt: 'Everything you need to know before tackling your first suspension lift, from tools to torque specs to common pitfalls.',
  date: 'March 15, 2024',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=800&q=80',
  category: 'Installation'
}, {
  id: 2,
  title: 'Shock Tuning 101: Understanding Compression and Rebound',
  excerpt: 'Learn how to dial in your shocks for the perfect balance between on-road comfort and off-road performance.',
  date: 'March 8, 2024',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?auto=format&fit=crop&w=800&q=80',
  category: 'Tech'
}, {
  id: 3,
  title: "Customer Spotlight: Mike's 2023 F-150 Build",
  excerpt: 'See how Mike transformed his daily driver into a capable weekend warrior with our 6" Pro-Stage 1 system.',
  date: 'February 28, 2024',
  readTime: '5 min read',
  image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?auto=format&fit=crop&w=800&q=80',
  category: 'Builds'
}, {
  id: 4,
  title: 'Why Alignment Matters After a Lift',
  excerpt: "Don't skip the alignment. Here's why proper geometry is critical for tire wear, handling, and safety.",
  date: 'February 20, 2024',
  readTime: '4 min read',
  image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=800&q=80',
  category: 'Maintenance'
}, {
  id: 5,
  title: 'Trail Report: Moab with the Pro One Team',
  excerpt: "Our crew took a fleet of lifted trucks to Moab to test new products. Here's what we learned on Hell's Revenge.",
  date: 'February 12, 2024',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=800&q=80',
  category: 'Adventures'
}, {
  id: 6,
  title: 'New Product: Billet Upper Control Arms for Ford Bronco',
  excerpt: "We're excited to announce our new billet upper control arms designed specifically for the 6th-gen Bronco.",
  date: 'February 5, 2024',
  readTime: '3 min read',
  image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?auto=format&fit=crop&w=800&q=80',
  category: 'News'
}];
export function MediaPage({
  onNavigate
}: MediaPageProps) {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const categories = ['All', ...Array.from(new Set(articles.map(a => a.category)))];
  const filtered = articles.filter(a => {
    const matchesCategory = filter === 'All' || a.category === filter;
    const matchesSearch = a.title.toLowerCase().includes(search.toLowerCase()) || a.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-516">
      <Header data-id="element-517" />
      <Navigation onNavigate={onNavigate} data-id="element-518" />

      <main className="flex-grow" data-id="element-519">
        {/* Hero */}
        <div className="bg-charcoal text-white py-16 relative overflow-hidden" data-id="element-520">
          <div className="absolute inset-0 bg-hero-pattern opacity-10 bg-cover bg-center" data-id="element-521" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" data-id="element-522" />
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 relative z-10" data-id="element-523">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-524">
              <button onClick={() => onNavigate('home')} className="hover:text-primary transition-colors" data-id="element-525">
                Home
              </button>
              <span data-id="element-526">/</span>
              <span className="text-white" data-id="element-527">Media</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-heading font-bold uppercase italic tracking-tighter" data-id="element-528">
              News & <span className="text-primary" data-id="element-529">Stories</span>
            </h1>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16" data-id="element-530">
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-10" data-id="element-531">
            Installation guides, customer builds, trail reports, and product
            announcements from the Pro One Design team.
          </p>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-532">
            <div className="relative flex-grow max-w-md" data-id="element-533">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-534" />
              <input type="text" placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 bg-white text-sm focus:outline-none focus:border-primary transition-colors" data-id="element-535" />
            </div>
            <div className="flex gap-2 flex-wrap" data-id="element-536">
              {categories.map(cat => <button key={cat} onClick={() => setFilter(cat)} className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors border
                    ${filter === cat ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-gray-500 border-gray-200 hover:border-charcoal hover:text-charcoal'}`} data-id="element-537">
                  {cat}
                </button>)}
            </div>
          </div>

          {/* Articles Grid */}
          {filtered.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-538">
              {filtered.map(article => <article key={article.id} className="bg-white border border-gray-200 flex flex-col group hover:border-primary/50 transition-all duration-300" data-id="element-539">
                  <div className="relative h-56 bg-gray-100 overflow-hidden" data-id="element-540">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-541" />
                    <div className="absolute top-3 left-3 bg-primary text-white text-xs font-bold px-3 py-1 uppercase italic skew-x-[-10deg]" data-id="element-542">
                      <span className="block skew-x-[10deg]" data-id="element-543">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-544">
                    <div className="flex items-center gap-4 text-xs text-gray-400 mb-3" data-id="element-545">
                      <span className="flex items-center gap-1" data-id="element-546">
                        <Calendar className="w-3 h-3" data-id="element-547" /> {article.date}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-548">
                        <Clock className="w-3 h-3" data-id="element-549" /> {article.readTime}
                      </span>
                    </div>
                    <h3 className="text-2xl font-heading font-bold text-charcoal uppercase italic mb-3 leading-tight group-hover:text-primary transition-colors" data-id="element-550">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-grow" data-id="element-551">
                      {article.excerpt}
                    </p>
                    <button className="flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-wider group-hover:gap-3 transition-all" data-id="element-552">
                      Read More <ArrowRight className="w-4 h-4" data-id="element-553" />
                    </button>
                  </div>
                </article>)}
            </div> : <div className="text-center py-16 bg-white border border-dashed border-gray-300" data-id="element-554">
              <p className="text-gray-400 font-heading font-bold text-xl uppercase italic" data-id="element-555">
                No articles match your search
              </p>
              <button onClick={() => {
            setSearch('');
            setFilter('All');
          }} className="mt-3 text-primary font-bold hover:underline text-sm" data-id="element-556">
                Clear Filters
              </button>
            </div>}
        </div>
      </main>

      <Footer onNavigate={onNavigate} data-id="element-557" />
    </div>;
}